<template>
  <div class="match-center">
    <!-- Every tab takes the tracker's height and scrolls inside it, so
         switching tab never makes the page below jump. -->
    <div class="active-widget-container" :class="{ 'is-fixed-height': paneHeight }" :style="paneHeight ? { height: paneHeight + 'px' } : null">
      <div v-if="currentTab === 'tracker'" ref="trackerPane">
        <MatchTracker :match-data="matchData" :event-id="eventId" :initial-sport-id="sportId" :is-expanded="isExpanded" :resizable="!lockExpanded" :show-momentum="showMomentum" :player-stats="playerStats" />
      </div>
      <StatsSection v-else-if="currentTab === 'stats'" class="tracker-data-tab" :event="matchData" />
      <PlayerStatsSection v-else-if="currentTab === 'playerstats'" class="tracker-data-tab" :match-data="matchData" :player-stats="playerStats" />
      <MatchTimeline v-else-if="currentTab === 'timeline'" class="tracker-data-tab" :match-data="matchData" :player-stats="playerStats" />
      <LineupSection v-else-if="currentTab === 'lineups'" class="tracker-data-tab" :match-data="matchData" :player-stats="playerStats" />
      <div v-else-if="currentTab && !getAuthenticated" class="widget-login-prompt">
        <button class="cta-button text-capitalize" @click="openLogin">
          {{ $t('loginToSeeWidgets') }}
        </button>
      </div>
      <WidgetTool
          v-else-if="currentTab === 'the_sport_match_tracker'"
          :sport-id="sportId"
          :event-id="eventId"
          @unavailable="matchTrackerUnavailable = true"
      />
      <StreamingTheSports
          v-else-if="currentTab === 'the_sport_streaming'"
          :sport-id="sportId"
          :event-id="eventId"
          @unavailable="streamingUnavailable = true"
      />
    </div>

    <!-- Tab bar under the active tab. -->
    <div v-if="widgetGroups.length > 1" class="widget-smart-toolbar-wrapper">
      <smart-toolbar
          :groups="widgetGroups"
          :group-selected="currentTab"
          :tabs-scrollable="true"
          :small-label="true"
          :center-tabs="true"
          :show-expand-collapse="false"
          :show-change-col="false"
          :show-search="false"
          @update:groupSelected="activeTabId = $event"
      />
    </div>
  </div>
</template>

<script>
import StreamingTheSports from "@/components/center_widgets/Live/StreamingTheSports.vue";
import WidgetTool from "@/components/center_widgets/Live/WidgetTool.vue";
import SmartToolbar from "@/components/generic/SmartToolbar.vue";
import MatchTracker from "@/components/generic/MatchTracker.vue";
import StatsSection from "@/components/center_widgets/Live/TradeTracker/StatsSection.vue";
import PlayerStatsSection from "@/components/center_widgets/Live/TradeTracker/PlayerStatsSection.vue";
import MatchTimeline from "@/components/center_widgets/Live/TradeTracker/MatchTimeline.vue";
import LineupSection from "@/components/center_widgets/Live/TradeTracker/LineUpSection.vue";
import {hasMatchTracker} from "@/services/matchtracker/matchTrackerSports";
import {subscribePlayerStats, inSquad} from "@/services/matchtracker/playerStatsPoller";
import axios from "axios";
import {mapGetters, mapActions} from "vuex";

// Tracker tab icon: a pitch drawn with lines only; the selected tab fills
// the turf (see .tab-pitch in the styles).
const PITCH_SVG = '<svg class="tab-pitch" viewBox="0 0 26 18" aria-hidden="true">' +
    '<rect class="tab-pitch__turf" x="1" y="1" width="24" height="16" rx="2.5"/>' +
    '<path class="tab-pitch__stripes" d="M5.8 1.4h3.2v15.2H5.8zM12.2 1.4h1.6v15.2h-1.6zM17 1.4h3.2v15.2H17z"/>' +
    '<g class="tab-pitch__lines">' +
    '<rect x="1" y="1" width="24" height="16" rx="2.5"/>' +
    '<path d="M13 1v16M1 5.5h3.6v7H1M25 5.5h-3.6v7H25M1 7.6h1.4v2.8H1M25 7.6h-1.4v2.8H25"/>' +
    '<circle cx="13" cy="9" r="2.8"/>' +
    '</g>' +
    '<circle class="tab-pitch__spot" cx="13" cy="9" r="0.9"/>' +
    '</svg>';

// Live match panel, the same on mobile (under the hero) and desktop (right
// column): tab bar with our tracker, the TheSports widgets and the tracker
// data (stats, chronology, lineups).
export default {
  name: "MatchCenter",
  components: {
    SmartToolbar,
    WidgetTool,
    StreamingTheSports,
    MatchTracker,
    StatsSection,
    PlayerStatsSection,
    MatchTimeline,
    LineupSection
  },
  props: {
    matchData: { type: Object, required: true },
    eventId: { type: [String, Number], required: true },
    sportId: { type: [String, Number], required: true },
    isExpanded: { type: Boolean, default: false },
    // Size fixed by the page: the tracker drops its fullscreen button.
    lockExpanded: { type: Boolean, default: false },
    // Momentum header above the tracker (hidden in the American module).
    showMomentum: { type: Boolean, default: true },
    // The page shows the tracker data tabs (stats, timeline...) in its own nav:
    // only the tracker and the TheSports widgets stay here, the data tabs and
    // the player stats go up with 'data-tabs' / 'player-stats'.
    splitDataTabs: { type: Boolean, default: false }
  },
  data() {
    return {
      activeTabId: null,
      matchTrackerUnavailable: false,
      streamingUnavailable: false,
      // widget-tool answer for this sport: true only once it says streaming works.
      streamingAvailable: false,
      trackerHeight: 0,
      // live-scores/player-stats answer: teams[].players[] with their stats.
      playerStats: null,
    };
  },
  watch: {
    currentTab: {
      immediate: true,
      handler() { this.$nextTick(this.observeTracker); }
    },
    // Player stats polling only while the tracker tabs are there.
    playerStatsEventId: {
      immediate: true,
      handler(id) { this.watchPlayerStats(id); }
    },
    streamingCheckKey: {
      immediate: true,
      handler() { this.checkStreaming(); }
    },
    dataTabs: {
      immediate: true,
      handler(tabs) { if (this.splitDataTabs) this.$emit('data-tabs', tabs); }
    },
    playerStats(data) {
      if (this.splitDataTabs) this.$emit('player-stats', data);
    },
    eventId() {
      this.activeTabId = null;
      this.matchTrackerUnavailable = false;
      this.streamingUnavailable = false;
    }
  },
  beforeDestroy() {
    if (this.trackerObserver) this.trackerObserver.disconnect();
    this.watchPlayerStats(null);
  },
  methods: {
    ...mapActions({ fetchPlayerStats: 'odds/fetchPlayerStats' }),
    // One subscription at a time: the old event is let go before the new one.
    watchPlayerStats(eventId) {
      if (this.unsubscribePlayerStats) this.unsubscribePlayerStats();
      this.unsubscribePlayerStats = null;
      this.playerStats = null;
      if (!eventId) return;
      this.unsubscribePlayerStats = subscribePlayerStats(eventId, id => this.fetchPlayerStats({ eventId: id }), data => {
        if (String(data.eventId ?? eventId) === String(this.playerStatsEventId)) this.playerStats = data;
      });
    },
    // The streaming tab shows only when widget-tool answers for the sport; an
    // error or {status: false} ("No widget found for this sport") hides it.
    async checkStreaming() {
      const requestId = this.streamingRequestId = (this.streamingRequestId || 0) + 1;
      this.streamingAvailable = false;
      if (!this.streamingEnabled || !this.sportId) return;
      try {
        const response = await axios.get(`${this.getUrl}/v3/widgets/widget-tool`, {
          params: { platformId: this.getPlatformId, sportId: this.sportId },
          headers: { Authorization: `Bearer ${this.getToken}` }
        });
        if (requestId !== this.streamingRequestId) return;
        const body = response.data;
        const widget = body?.data || body;
        this.streamingAvailable = !!body && body.status !== false && !!widget && widget.streamEnabled !== false;
      } catch (err) {
        if (requestId === this.streamingRequestId) this.streamingAvailable = false;
      }
    },
    // Keeps the last height the tracker rendered at: the other tabs reuse it.
    observeTracker() {
      if (this.trackerObserver) this.trackerObserver.disconnect();
      const el = this.$refs.trackerPane;
      if (!el) return;
      this.trackerHeight = el.offsetHeight;
      if (typeof ResizeObserver === 'undefined') return;
      this.trackerObserver = new ResizeObserver(() => {
        if (el.offsetHeight) this.trackerHeight = el.offsetHeight;
      });
      this.trackerObserver.observe(el);
    },
    openLogin() {
      window.parent.postMessage({ type: 'OPEN_LOGIN' }, '*');
    }
  },
  computed: {
    ...mapGetters({
      'getUiPersonalizationGeneral': 'settings/getUiPersonalizationGeneral',
      'getAuthenticated': 'user/getAuthenticated',
      'getToken': 'settings/getToken',
      'getUrl': 'settings/getUrlApi',
      'getPlatformId': 'settings/getPlatformId',
    }),
    orderedLiveWidgets() {
      const config = this.getUiPersonalizationGeneral;
      const validIds = ['the_sport_match_tracker', 'the_sport_streaming'];
      if (!config) {
        return [
          { id: 'the_sport_match_tracker', enabled: true, order: 1 },
          { id: 'the_sport_streaming', enabled: true, order: 2 }
        ];
      }
      return config
          .filter(w => validIds.includes(w.id))
          .map(w => ({ id: w.id, enabled: w.enabled, order: w.order }))
          .sort((a, b) => a.order - b.order);
    },
    enabledLiveWidgets() {
      return this.orderedLiveWidgets.filter(w => w.enabled);
    },
    // Streaming only when the skin config lists it and enables it (no default).
    streamingCheckKey() {
      return `${this.streamingEnabled}-${this.sportId}-${this.getToken}`;
    },
    streamingEnabled() {
      const config = this.getUiPersonalizationGeneral;
      return Array.isArray(config) && config.some(w => w.id === 'the_sport_streaming' && w.enabled === true);
    },
    // Our tracker takes the field icon (TheSports' tracker keeps it only for
    // sports we have no scene for), then the streaming and the tracker data
    // as text tabs.
    widgetGroups() {
      const available = id => this.enabledLiveWidgets.some(w => w.id === id) &&
          !(id === 'the_sport_match_tracker' && this.matchTrackerUnavailable) &&
          !(id === 'the_sport_streaming' && this.streamingUnavailable);
      const groups = [];
      if (this.hasTracker) groups.push({ _id: 'tracker', svg: PITCH_SVG });
      else if (available('the_sport_match_tracker')) groups.push({ _id: 'the_sport_match_tracker', svg: PITCH_SVG });
      if (this.streamingEnabled && this.streamingAvailable && !this.streamingUnavailable) groups.push({ _id: 'the_sport_streaming', icon: 'mdi-play-circle-outline' });
      return this.splitDataTabs ? groups : groups.concat(this.dataTabs);
    },
    // Tracker data as text tabs, only with our tracker.
    dataTabs() {
      if (!this.hasTracker) return [];
      const tabs = [{ _id: 'stats', name: this.$t('statistics.tabstats') }];
      if (this.hasPlayerStats) tabs.push({ _id: 'playerstats', name: this.$t('statistics.tabplayerstats') });
      tabs.push({ _id: 'timeline', name: this.$t('statistics.tabtimeline') });
      if (this.hasLineups) tabs.push({ _id: 'lineups', name: this.$t('statistics.tablineups') });
      return tabs;
    },
    // Player stats tab when a player has played, per the player-stats API or
    // the feed's playerStatistics.
    hasPlayerStats() {
      if (this.apiPlayers.some(p => p.hasPlayed)) return true;
      const ls = this.matchData?.Livescore || this.matchData?.livescore;
      const list = this.matchData?.playerStatistics || ls?.playerStatistics || ls?.PlayerStatistics;
      return Array.isArray(list) && list.length > 0;
    },
    playerStatsEventId() {
      return this.hasTracker ? String(this.eventId || '') : '';
    },
    apiPlayers() {
      return (this.playerStats?.teams || []).flatMap(t => Array.isArray(t.players) ? t.players : []);
    },
    // Lineups tab when the player-stats API sends players, or event-detail
    // sends players in the match squad (a player without state is not in it,
    // see LineupSection).
    hasLineups() {
      if (this.apiPlayers.some(inSquad)) return true;
      return (this.matchData?.competitors || []).some(c => Array.isArray(c.players) && c.players.some(p => p && p.state));
    },
    // The picked tab, or the first one when it is gone (widget unavailable).
    currentTab() {
      const groups = this.widgetGroups;
      return groups.some(g => g._id === this.activeTabId) ? this.activeTabId : (groups[0]?._id ?? null);
    },
    // The tracker sets its own height; the other tabs get the one it left.
    paneHeight() {
      return this.currentTab !== 'tracker' && this.trackerHeight ? this.trackerHeight : 0;
    },
    // MatchTracker only when the skin enables it, for the sports it has a scene for.
    hasTracker() {
      return hasMatchTracker(this.matchData?.sportId ?? this.sportId, this.getUiPersonalizationGeneral);
    }
  }
}
</script>

<style scoped>
.match-center ::v-deep .market-indicator {
  background: var(--color-accent);
}

/* Tracker tab pitch: outline only, filled turf once selected. */
.match-center ::v-deep .tab-pitch {
  display: block;
  width: 20px;
  height: 14px;
  overflow: visible;
}

.match-center ::v-deep .tab-pitch__turf {
  fill: transparent;
  transition: fill 0.2s ease;
}

.match-center ::v-deep .tab-pitch__stripes {
  fill: #000;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.match-center ::v-deep .tab-pitch__lines {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.2;
  stroke-linejoin: round;
  transition: stroke 0.2s ease;
}

.match-center ::v-deep .tab-pitch__spot {
  fill: currentColor;
  transition: fill 0.2s ease;
}

.match-center ::v-deep .active-market .tab-pitch__turf { fill: var(--color-primary); }
.match-center ::v-deep .active-market .tab-pitch__stripes { opacity: 0.14; }
.match-center ::v-deep .active-market .tab-pitch__lines { stroke: #fff; }
.match-center ::v-deep .active-market .tab-pitch__spot { fill: #fff; }

/* Same frame the tracker gives these sections in its own secondary view. */
.tracker-data-tab {
  min-height: 100%;
  background: rgba(15, 15, 20, 0.4);
  padding: 10px;
}

.active-widget-container.is-fixed-height {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* One scroll only: drop the sections' own short scroll boxes in here. */
.is-fixed-height ::v-deep .lineups {
  max-height: none;
  overflow: visible;
}

.widget-login-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 36px 16px;
  margin: 8px 16px;
  border-radius: 12px;
  text-align: center;
}
</style>
